import { Modal, LoadingIndicator } from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import { useState } from "react";
import { SiteModelPriorityOption } from "components/atoms/SiteModelPriorityOption";
import { useAppSelector } from "store";
import { siteModelRequestDesignUpdatePriority } from "services/siteModel";
import { ErrorComponent } from "components/atoms/ErrorComponent";

type SiteModelPriorityModalProps = {
  onClose: () => void;
  status?: React.ReactNode;
  prospectId: string;
  designRequestId: string;
  refresh: () => void;
};

const SiteModelPriorityModal = ({
  onClose,
  status,
  prospectId,
  designRequestId,
  refresh,
}: SiteModelPriorityModalProps) => {
  const jwt = useAppSelector((state) => state?.auth?.hybridToken);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  const onRequest = async (priority: "high" | "normal") => {
    setError(undefined);
    setLoading(true);

    try {
      await siteModelRequestDesignUpdatePriority(
        jwt,
        prospectId,
        designRequestId,
        priority
      );

      await refresh();
      onClose();
    } catch (e) {
      setLoading(false);

      setError(
        "Sorry. We cannot complete your site model priority request at this time. Please try again or call sales support for assistance."
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <StyledModal
      testId="site-model-priority-modal"
      onClose={onClose}
      title="Are you sure you want to request priority?"
      hideIcon={true}
      customModalWidth={800}
    >
      {loading && <LoadingIndicator color="black" />}
      {error && (
        <ErrorComponent
          context="SiteModelPriorityModal"
          action="Request Priority"
          error={new Error(error)}
        />
      )}
      {!loading && (
        <Options>
          <SiteModelPriorityOption
            title="High Priority"
            priority={8}
            onClick={() => onRequest("high")}
            description={[
              "By requesting this option, you will be getting ahead of your current queue position. ",
              "Please note this option prioritizes speed with acceptable accuracy, this option should only be used when you are live with the customer",
            ]}
          />
          <SiteModelPriorityOption
            title="Normal Priority"
            priority={20}
            status={status}
            recommended
            description={[
              "With this option, the site modeling would have additional time to lean into quality, ensuring accuracy from the start.",
            ]}
          />
        </Options>
      )}
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  .modal-content-container {
    min-height: 400px !important;
    > div {
      padding-top: 160px;
    }
  }
`;

const Options = styled.div`
  display: flex;
  flex-direction: row;
  gap: 40px;

  > div {
    flex: 1;
  }
`;

export { SiteModelPriorityModal };
